import React, { useState, useRef, useEffect } from "react";
import "./Tooltip.css";

interface TooltipProps {
  /** Контент, который будет обёрнут тултипом */
  children: React.ReactElement;
  /** Текст или JSX для отображения в тултипе */
  content: React.ReactNode;
  /** Позиция тултипа относительно элемента */
  position?: "top" | "bottom" | "left" | "right";
  /** Задержка перед появлением (мс) */
  delay?: number;
  /** Максимальная ширина тултипа (px) */
  maxWidth?: number;
  /** Дополнительный класс для стилизации */
  className?: string;
}

const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  position = "top",
  delay = 200,
  maxWidth = 300,
  className = "",
}) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  let timeoutId: NodeJS.Timeout | null = null;

  const showTooltip = (e: React.MouseEvent<HTMLElement>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      const element = wrapperRef.current;
      if (!element) return;
      const rect = element.getBoundingClientRect();
      let x = rect.left + rect.width / 2;
      let y = rect.top;

      if (position === "top") {
        y = rect.top;
      } else if (position === "bottom") {
        y = rect.bottom;
      } else if (position === "left") {
        x = rect.left;
        y = rect.top + rect.height / 2;
      } else if (position === "right") {
        x = rect.right;
        y = rect.top + rect.height / 2;
      }

      setCoords({ x, y });
      setVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutId) clearTimeout(timeoutId);
    setVisible(false);
  };

  // Обработка клика вне тултипа для скрытия
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <span
        ref={wrapperRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onClick={hideTooltip}
        style={{ display: "inline-block" }}
      >
        {children}
      </span>
      {visible && (
        <div
          ref={tooltipRef}
          className={`tooltip tooltip-${position} ${className}`}
          style={{
            left: `${coords.x}px`,
            top: `${coords.y}px`,
            maxWidth: `${maxWidth}px`,
          }}
        >
          <div className="tooltip-content">{content}</div>
          <div className="tooltip-arrow" />
        </div>
      )}
    </>
  );
};

export default Tooltip;
